import { useAppContext } from "../../hooks/useAppContext";
import { useRef, useState, useEffect } from "react";
import { InputQuestion } from "../../components/InputQuestion";
import Info from "../../components/SVGIcons/Info";
import { UserChatMessage } from "../../components/UserChatMessage";
import { Answer, Thumb } from "../../components/Answer";
import Modal from "../../components/ui/Modal";
import ModalChatBot from "../../components/ModalChatBot";
import Redo from "../../components/SVGIcons/Redo";
import ChatProfile from "../../components/ChatProfile";
import { AnswerLoading } from "../../components/AnswerLoading";
import useBoundStore from "../../store/useBoundStore";
import { getTranslation } from "../../locales/getTranslation";
import Warning from "../../components/Warning";

const Chat = () => {
  const executeChatRequest = useBoundStore((state) => state.executeChatRequest);
  const { brand, locale, ipGeoLocation } = useAppContext();
  const user = useBoundStore((state) => state.user);
  const lastQuestion = useBoundStore((state) => state.lastQuestion);
  const answers = useBoundStore((state) => state.answers);
  const isLoading = useBoundStore((state) => state.isLoading);
  const chatHistoryPanel = useBoundStore((state) => state.chatHistoryPanel);
  const [question, setQuestion] = useState<string>("");
  const [infoOverlayOpen, setInfoOverlayOpen] = useState<boolean>(false);
  const [overlayContent, setOverlayContent] = useState<JSX.Element>(<></>);
  const [tokens, setTokens] = useState<number>(0);
  const [settingPanel, setSettingPanel] = useState(false);
  const loadingQuestionRef = useRef<HTMLDivElement | null>(null);
  const lastQuestionRef = useRef<HTMLDivElement | null>(null);

  const greeting = () => {
    const currentHour = new Date().getHours(); // 0 - 23
    let greeting = "";

    if (currentHour < 12) {
      greeting = getTranslation(locale, "Good Morning");
    } else if (currentHour < 18) {
      greeting = getTranslation(locale, "Good Afternoon");
    } else {
      greeting = getTranslation(locale, "Good Evening");
    }

    return greeting;
  };

  useEffect(() => {
    const target = isLoading
      ? loadingQuestionRef.current
      : lastQuestionRef.current;

    if (target) {
      // We use requestAnimationFrame to wait for the next paint
      requestAnimationFrame(() => {
        target.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      });
    }
  }, [isLoading, answers]);

  return (
    <>
      <div
        className={`relative flex h-full flex-col items-center justify-between gap-1`}
      >
        {!answers[0] ? (
          <div className="flex h-full w-full max-w-[800px] flex-col items-center justify-center gap-20">
            <div className="flex w-full flex-col items-center gap-3.5">
              <h1 className="w-full text-center text-4xl font-normal leading-[68px] text-grayscale-solid-g10 sm:text-[40px] md:text-5xl lg:text-[64px]">
                {greeting()}
                {user?.name && ", "}
                <span className="font-bold leading-[68px] text-blue-solid-blue6">
                  {user?.name && `${user.name}`}
                </span>
              </h1>
              <p className="w-full text-center text-xl font-normal leading-9 text-grayscale-solid-g10">
                {getTranslation(locale, "How may I assist you today?")}
              </p>
            </div>
            {answers.length == 0 && (
              <div
                className={`flex w-full flex-col items-center justify-center gap-1.5 pb-3`}
              >
                <InputQuestion
                  question={question}
                  setQuestion={setQuestion}
                  tokens={tokens}
                  setTokens={setTokens}
                  settingPanel={settingPanel}
                  setSettingPanel={setSettingPanel}
                  onSend={(question) =>
                    executeChatRequest(
                      question,
                      brand,
                      locale,
                      user,
                      ipGeoLocation,
                    )
                  }
                  disabled={isLoading}
                  firstQuestion={isLoading && !answers[0]}
                  placeholder={getTranslation(locale, "Ask a question...")}
                />
                <button
                  className="group flex cursor-pointer gap-1.5 md:hidden"
                  onClick={() => {
                    if (window.innerWidth < 1280) {
                      setInfoOverlayOpen(!infoOverlayOpen);
                      setOverlayContent(<ModalChatBot />);
                    }
                  }}
                >
                  <p className="text-center text-[12px] font-bold leading-tight text-blue-solid-blue6 group-hover:text-blue-solid-blue5">
                    {getTranslation(locale, "Chatbot Beta Service")}
                  </p>
                  <Info className="h-4 w-4 fill-blue-solid-blue6 group-hover:fill-blue-solid-blue5" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-[1000px]">
            {answers.map((answer, index) => {
              const isLast = index === answers.length - 1;

              return (
                <div key={index} ref={isLast ? lastQuestionRef : null}>
                  {/* Question */}
                  <UserChatMessage message={answer[0]} />
                  {/* Answer */}
                  <Answer
                    answer={answer[1]}
                    onFollowupQuestionClicked={(q) =>
                      executeChatRequest(q, brand, locale, user, ipGeoLocation)
                    }
                    redoButton={
                      <Redo
                        className="h-[18px] w-[18px] cursor-pointer fill-grayscale-solid-g10 hover:fill-blue-solid-blue6"
                        question={answer[0]}
                        disabled={isLoading}
                        onSend={(question) =>
                          executeChatRequest(
                            question,
                            brand,
                            locale,
                            user,
                            ipGeoLocation,
                          )
                        }
                      />
                    }
                  />
                </div>
              );
            })}

            {isLoading && (
              <div className="h-[calc(100vh-170px)]">
                <UserChatMessage
                  ref={loadingQuestionRef}
                  message={lastQuestion || ""}
                />
                <div className="flex flex-col gap-3 py-8">
                  <ChatProfile />
                  <div
                    className={`ml-4 flex h-[54px] w-[74px] items-center justify-center gap-4 rounded-br-[20px] rounded-tl-[20px] rounded-tr-[20px] bg-grayscale-solid-elevate-level2 p-4 shadow`}
                  >
                    <AnswerLoading className="bg-blue-solid-blue6" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {answers.length > 0 && (
          <div
            className={`fixed bottom-0 left-0 z-10 ${chatHistoryPanel ? "w-full sm:ml-[265px] sm:w-[calc(100vw-274px)]" : "w-full sm:ml-[51px] sm:w-[calc(100vw-60px)]"} flex justify-center px-2 transition-none sm:px-5 sm:transition-all sm:duration-[250ms] md:px-10 lg:px-16`}
          >
            <div className="flex w-full max-w-[1000px] flex-col items-center justify-center gap-1.5 pb-2.5">
              <InputQuestion
                question={question}
                setQuestion={setQuestion}
                tokens={tokens}
                setTokens={setTokens}
                settingPanel={settingPanel}
                setSettingPanel={setSettingPanel}
                onSend={(question) =>
                  executeChatRequest(
                    question,
                    brand,
                    locale,
                    user,
                    ipGeoLocation,
                  )
                }
                disabled={isLoading}
                firstQuestion={isLoading && !answers[0]}
                placeholder={getTranslation(locale, "Ask a question...")}
              />
              <button
                className="group flex cursor-pointer gap-1.5 md:hidden"
                onClick={() => {
                  if (window.innerWidth < 1280) {
                    setInfoOverlayOpen(!infoOverlayOpen);
                    setOverlayContent(<ModalChatBot />);
                  }
                }}
              >
                <p className="group-hover: text-center text-[12px] font-bold leading-tight text-blue-solid-blue6">
                  {getTranslation(locale, "Chatbot Beta Service")}
                </p>
                <Info className="h-4 w-4 fill-blue-solid-blue6 group-hover:fill-blue-solid-blue5" />
              </button>
              <Warning />
            </div>
          </div>
        )}
      </div>

      <Modal
        className="w-[325px]"
        open={infoOverlayOpen}
        setOpen={setInfoOverlayOpen}
      >
        {overlayContent}
      </Modal>
    </>
  );
};

export default Chat;
